// Shared message shape between the parent (SandboxFrame in the ontology
// UI) and the sandbox host. Kept tiny on purpose — anything richer
// invites validation drift between the two origins.
//
// Four message families flow between parent and sandbox:
//
//   1. Init handshake (sandbox → parent `sandbox-needs-init`; parent →
//      sandbox `sandbox-init`). On mount the sandbox asks the parent
//      for the user's API key or identity token, the proxy server base
//      URL, and the current ontology branch. The sandbox refuses to
//      render any `sandbox-render` payload until it has received an
//      authenticated `sandbox-init` message.
//
//   2. Render lifecycle (parent → sandbox `sandbox-render`; sandbox →
//      parent `sandbox-ready` / `sandbox-error`). Used to ship code +
//      props + theme into the iframe and surface compile/render errors.
//
//   3. Templates registry (parent → sandbox `sandbox-templates`).
//      Carries the list of saved templates that the in-scope
//      `<Template path="..." />` primitive resolves against. Sent
//      separately from `sandbox-render` because the registry usually
//      changes far less often than the rendered code; the host can
//      push a new registry without re-shipping the current dashboard.
//
//   4. Navigation (sandbox → parent `sandbox-navigate`). A click on an
//      anchor inside rendered dashboard code posts the raw `href` so the
//      parent can move the whole Axilon page. The iframe never follows
//      the link itself: its document URL is the sandbox origin, so a
//      root-relative `/plant` path would otherwise leave the dashboard
//      host. Hash-only hrefs stay in the iframe.
//
//   5. (None of the above.) The sandbox makes its own /api/v1/ontology/*
//      fetches directly using the credential it received in `sandbox-init`.
//      There is no per-request RPC bridge.

export type SandboxTheme = 'light' | 'dark'

export interface SandboxNeedsInitMessage {
  type: 'sandbox-needs-init'
}

export interface SandboxInitMessage {
  type: 'sandbox-init'
  /**
   * The user's tenant API key, taken from the parent UI's localStorage
   * (`ontology-api-key` — set via the Settings page). Forwarded on
   * every sandbox-originated proxy request as `X-API-Key`. An empty
   * string is valid when `accessToken` supplies identity authentication;
   * otherwise this credential takes precedence over `accessToken`.
   */
  apiKey: string
  /**
   * Identity access token from the parent UI. Forwarded as
   * `Authorization: Bearer ...` only when `apiKey` is empty. Optional for
   * wire compatibility with older API-key-only parents; an empty/missing
   * value disables JWT auth.
   */
  accessToken?: string
  /**
   * Organization claim associated with the parent session. Forwarded as
   * `X-Organization-ID` whenever present. The proxy uses this header for
   * API-key requests; JWT requests derive tenant identity from the verified
   * token and ignore the header.
   */
  organizationId?: string
  /**
   * Absolute proxy server base URL, or empty for "same-origin" (dev,
   * where Vite proxies `/api` to the proxy server). When non-empty,
   * the sandbox uses `${proxyBaseUrl}/api/v1/ontology/...`; otherwise
   * it falls back to a relative `/api/v1/ontology/...` path.
   */
  proxyBaseUrl: string
  /**
   * Current ontology branch the parent has selected. Sent as the
   * `X-Ontology-Branch` header on every sandbox-originated request.
   * Empty string omits the header (proxy applies its default branch).
   */
  branch: string
  /**
   * Current ontology site the parent has selected. Sent as the
   * `X-Ontology-Site` header on every sandbox-originated request.
   * `*` means all-sites; empty string omits the header.
   */
  site: string
  /**
   * Saved dashboard id for operator database writes. Omitted for templates,
   * chat artifacts, and headless screenshots, which cannot write.
   */
  dashboardId?: string
}

/**
 * One entry in the templates registry. The sandbox compiles each
 * entry's `code` lazily (memoized by code string) the first time it
 * sees a `<Template path="..." />` referencing this path, then
 * spreads `defaultValues` into the resolved component as initial
 * props (caller-supplied props from `<Template />` JSX override
 * matching keys).
 */
export interface SandboxTemplateEntry {
  path: string
  code: string
  defaultValues: Record<string, unknown>
}

/**
 * Parent → sandbox: replace the sandbox's templates registry. The
 * `<Template path="..." />` primitive in render scope looks templates
 * up here. An empty array clears the registry and any subsequent
 * `<Template />` resolves to a "no template at that path" error.
 */
export interface SandboxTemplatesMessage {
  type: 'sandbox-templates'
  templates: SandboxTemplateEntry[]
}

export interface SandboxRenderMessage {
  type: 'sandbox-render'
  code: string
  theme: SandboxTheme
  /**
   * Optional props spread into the rendered component as
   * `<Component {...props} />`. Used by the Templates editor to
   * preview a template with its stored default values; dashboards
   * leave this undefined.
   */
  props?: Record<string, unknown>
}

/**
 * Parent → sandbox: run a one-shot data/transformation script (NOT a React
 * component). The script default-exports an async function whose resolved
 * value is the result; it runs with the same async data functions
 * (`fetchTagValues`, `fetchTagHistory`, …) the dashboard hooks call under
 * the hood, so a transformation validated here behaves identically once it
 * is later wired into dashboard TSX. The sandbox replies with a matching
 * `sandbox-script-result` carrying the same `requestId`.
 */
export interface SandboxRunScriptMessage {
  type: 'sandbox-run-script'
  /** Correlates the reply with this request (multiple may be in flight). */
  requestId: string
  /** Script source (TS/JS). Must default-export an async function (or a value). */
  code: string
}

/**
 * Sandbox → parent: the outcome of a `sandbox-run-script`. `ok` is false
 * when the script threw or failed to compile (`error` is then populated).
 * `resultJson` is the JSON-serialized return value, capped in size;
 * `truncated` is true when the value exceeded the cap (in which case
 * `resultJson` is a best-effort truncated string, not valid JSON).
 */
export interface SandboxScriptResultMessage {
  type: 'sandbox-script-result'
  requestId: string
  ok: boolean
  resultJson: string | null
  truncated: boolean
  logs: string[]
  error: string | null
}

export interface SandboxReadyMessage {
  type: 'sandbox-ready'
}

/**
 * Sandbox → parent: emitted once a `sandbox-init` with a non-empty API key or
 * access token has been applied (auth is live, hooks can fetch). Unlike `sandbox-ready`
 * (posted on mount, before auth), this is a deterministic "you can now ask
 * me to render real data" signal. The interactive editor ignores it; the
 * headless screenshot capturer waits for it before requesting a render.
 */
export interface SandboxInitializedMessage {
  type: 'sandbox-initialized'
}

export interface SandboxErrorMessage {
  type: 'sandbox-error'
  message: string
}

/**
 * Sandbox → parent: the user activated an anchor in dashboard code.
 * `href` is the raw attribute (not resolved against the sandbox
 * document URL). `newTab` is set for `target="_blank"`, a middle click,
 * or a modifier click. The parent decides whether the href may move
 * the top-level page.
 */
export interface SandboxNavigateMessage {
  type: 'sandbox-navigate'
  href: string
  newTab: boolean
}

export type SandboxInboundMessage =
  | SandboxInitMessage
  | SandboxRenderMessage
  | SandboxRunScriptMessage
  | SandboxTemplatesMessage
export type SandboxOutboundMessage =
  | SandboxNeedsInitMessage
  | SandboxReadyMessage
  | SandboxInitializedMessage
  | SandboxErrorMessage
  | SandboxScriptResultMessage
  | SandboxNavigateMessage

export function isSandboxInitMessage(value: unknown): value is SandboxInitMessage {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  return (
    obj.type === 'sandbox-init' &&
    typeof obj.apiKey === 'string' &&
    (obj.accessToken === undefined || typeof obj.accessToken === 'string') &&
    (obj.organizationId === undefined || typeof obj.organizationId === 'string') &&
    typeof obj.proxyBaseUrl === 'string' &&
    typeof obj.branch === 'string' &&
    typeof obj.site === 'string' &&
    (obj.dashboardId === undefined || typeof obj.dashboardId === 'string')
  )
}

export function isSandboxRenderMessage(value: unknown): value is SandboxRenderMessage {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  if (obj.type !== 'sandbox-render') return false
  if (typeof obj.code !== 'string') return false
  if (obj.theme !== 'light' && obj.theme !== 'dark') return false
  // `props` is optional and, when present, must be a plain object —
  // the sandbox spreads it into `<Component {...props} />`, so an
  // array / scalar / null would crash the renderer.
  if (obj.props !== undefined) {
    if (obj.props === null || typeof obj.props !== 'object' || Array.isArray(obj.props)) {
      return false
    }
  }
  return true
}

export function isSandboxNeedsInitMessage(value: unknown): value is SandboxNeedsInitMessage {
  if (typeof value !== 'object' || value === null) return false
  return (value as Record<string, unknown>).type === 'sandbox-needs-init'
}

export function isSandboxRunScriptMessage(value: unknown): value is SandboxRunScriptMessage {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  return (
    obj.type === 'sandbox-run-script' &&
    typeof obj.requestId === 'string' &&
    typeof obj.code === 'string'
  )
}

export function isSandboxTemplatesMessage(value: unknown): value is SandboxTemplatesMessage {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  if (obj.type !== 'sandbox-templates') return false
  if (!Array.isArray(obj.templates)) return false
  for (const entry of obj.templates) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) return false
    const e = entry as Record<string, unknown>
    if (typeof e.path !== 'string' || typeof e.code !== 'string') return false
    if (
      e.defaultValues === null ||
      typeof e.defaultValues !== 'object' ||
      Array.isArray(e.defaultValues)
    ) {
      return false
    }
  }
  return true
}
